import { useField } from 'formik';
import { getInitials } from 'utils/helpers';
import Select, { components as C } from 'react-select';

const AVATAR = 'flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white border border-gray-200 shadow-md';

const Logo = ({ src, alt }) => (
  <span className={`${AVATAR} text-xs font-semibold text-gray-400`}>
    {src ? <img src={src} alt={alt} className="h-full w-full object-cover" /> : getInitials(alt)}
  </span>
);

const LogoOption = (props) => (
  <C.Option {...props}>
    <span className="flex items-center gap-2.5">
      <Logo src={props.data.logo} alt={props.data.label} />
      <span className="text-sm font-medium">{props.data.label}</span>
    </span>
  </C.Option>
);

const LogoSingleValue = (props) => (
  <C.SingleValue {...props}>
    <span className="flex min-w-0 items-center gap-2.5">
      <Logo src={props.data.logo} alt={props.data.label} />
      <span className="truncate text-sm">{props.data.label}</span>
    </span>
  </C.SingleValue>
);

const SelectField = ({ name, label, placeholder, options = [] }) => {
  const [field, meta, helpers] = useField(name);

  const matched = options.find((o) => o.value === field.value) ?? null;
  const fallback = !matched && field.value ? { value: field.value, label: field.value } : null;
  const selected = matched ?? fallback;
  const allOptions = fallback ? [...options, fallback] : options;
  const showError = meta.touched && Boolean(meta.error);
  const withLogos = options.some((o) => 'logo' in o);

  return (
    <div className="form-control">
      <label className="label pb-1.5">
        <span className="label-text font-medium">{label}</span>
      </label>

      <Select
        unstyled
        isClearable
        placeholder={placeholder}
        options={allOptions}
        value={selected}
        onChange={(option) => helpers.setValue(option?.value ?? '')}
        onBlur={() => helpers.setTouched(true)}
        components={withLogos ? { Option: LogoOption, SingleValue: LogoSingleValue } : undefined}
        classNames={{
          control: () => `input input-bordered flex w-full flex-nowrap items-center gap-2 ${showError ? 'input-error' : ''}`,
          valueContainer: () => 'min-w-0 !overflow-visible',
          singleValue: () => 'min-w-0 !overflow-visible',
          placeholder: () => 'text-gray-400',
          dropdownIndicator: () => 'shrink-0 text-gray-400',
          clearIndicator: () => 'shrink-0 text-gray-400 hover:text-gray-600',
          indicatorSeparator: () => 'hidden',
          menu: () => 'absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-base-300 bg-white shadow-lg',
          menuList: () => 'p-1.5 max-h-72 overflow-y-auto',
          option: ({ isSelected, isFocused }) =>
            `rounded-lg px-3 py-2 cursor-pointer ${isSelected ? 'bg-gray-100 font-medium' : isFocused ? 'bg-gray-50' : ''}`,
        }}
      />

      {showError && (
        <label className="label pt-1">
          <span className="label-text-alt text-error">{meta.error}</span>
        </label>
      )}
    </div>
  );
};

export default SelectField;
